import * as React from 'react';

import { Button as ShadcnButton } from '@/hen-ui/shadcn/button';
import { buttonVariants, type ButtonVariantProps } from './button.variants';
import { Icon, type LucideIconName } from '@/hen-ui/atomics/Icon';
import { cn } from '@/hen-ui/utils';

export interface ButtonProps
  extends Omit<
      React.ComponentPropsWithoutRef<typeof ShadcnButton>,
      'variant' | 'size'
    >,
    ButtonVariantProps {
  /**
   * Icon to display before the button text.
   * Must be a valid Lucide icon name (e.g., 'plus', 'arrow-right', 'download').
   * @example
   * <Button iconBefore="plus">Add Item</Button>
   */
  iconBefore?: LucideIconName;
  /**
   * Icon to display after the button text.
   * Must be a valid Lucide icon name (e.g., 'plus', 'arrow-right', 'download').
   * @example
   * <Button iconAfter="arrow-right">Continue</Button>
   */
  iconAfter?: LucideIconName;
  href?: string;
}

/**
 * Button component following HEN Design System standards.
 *
 * A thin wrapper around shadcn/ui button with HEN-specific variants and design tokens.
 * All styling uses tokens from tailwind.config.ts.
 *
 * @example
 * // Basic usage
 * <Button>Click me</Button>
 *
 * @example
 * // With HEN variant and size
 * <Button variant="primaryOutline" size="lg">
 *   Large outlined button
 * </Button>
 *
 * @example
 * // As a link using asChild
 * <Button asChild variant="blackFill">
 *   <a href="/about">About</a>
 * </Button>
 *
 * @example
 * // With icons (import from lucide-react)
 * import { Plus } from 'lucide-react';
 * <Button variant="primaryFill">
 *   <Plus /> Create New
 * </Button>
 *
 * @example
 * // With iconBefore prop
 * <Button variant="primaryFill" iconBefore="plus">
 *   Add Item
 * </Button>
 *
 * @example
 * // With iconAfter prop
 * <Button variant="primaryOutline" iconAfter="arrow-right">
 *   Continue
 * </Button>
 */
export const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      className,
      variant,
      size,
      iconBefore,
      iconAfter,
      children,
      asChild,
      href,
      ...props
    },
    ref,
  ) => {
    const content = (
      <>
        {iconBefore && <Icon icon={iconBefore} aria-hidden={true} />}
        {children}
        {iconAfter && <Icon icon={iconAfter} aria-hidden={true} />}
      </>
    );

    if (href) {
      // When href is present the component renders as an <a>. We cast props to
      // anchor attributes so all forwarded props (onClick, target, rel, aria-*,
      // data-*, etc.) reach the element — nothing is silently dropped.
      const anchorProps =
        props as React.AnchorHTMLAttributes<HTMLAnchorElement>;
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          {...anchorProps}
          href={href}
          className={cn(buttonVariants({ variant, size }), className)}
        >
          {content}
        </a>
      );
    }

    return (
      <ShadcnButton
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref as React.Ref<HTMLButtonElement>}
        asChild={asChild}
        {...props}
      >
        {asChild ? children : content}
      </ShadcnButton>
    );
  },
);

Button.displayName = 'Button';
